import { createStore, createEffect, createEvent, combine } from 'effector';
import { ApiUtils } from '@shared/utils';
import { AccountsApi, RequestStrategyEnum, type AccountRequest, type AccountResponse, type WithSignal } from '@shared/api';

const getAccountsEffect = createEffect(({ signal, ...request }: WithSignal<AccountRequest> = {}) => AccountsApi.getAccounts(request, signal));
const clearEvent = createEvent();

const $dataStore = createStore<AccountResponse[]>([])
  .on(getAccountsEffect.done, (_, data) => data.result.data ?? [])
  .reset(clearEvent);

const $prevCurrencyStore = createStore<string | undefined>(undefined, { skipVoid: false })
  .on(getAccountsEffect.done, (_, { params }) => (params && 'Currency' in params ? params.Currency : undefined))
  .on(getAccountsEffect.fail, (state, { params, error }) =>
    ApiUtils.isAbortError(error) ? state : params && 'Currency' in params ? params.Currency : undefined,
  )
  .reset(clearEvent);

const $loadedStore = createStore<boolean>(false)
  .on(getAccountsEffect.done, () => true)
  .on(getAccountsEffect.fail, (state, data) => (ApiUtils.isAbortError(data.error) ? state : true))
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getAccountsEffect.pending, (_, pending) => pending)
  .on(getAccountsEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getAccountsEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getAccountsEffect, () => null)
  .on(getAccountsEffect.fail, () => 'Something went wrong while fetching Accounts. Please try again.')
  .on(getAccountsEffect.done, () => null)
  .reset(clearEvent);

const getAccountsApi = ApiUtils.wrapperApi(getAccountsEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getAccountsApi.cancel());

export const $accountListStore = {
  events: {
    getAccounts: getAccountsApi.trigger,
    clear: clearEvent,
  },
  store: combine({
    loaded: $loadedStore,
    loading: $loadingStore,
    prevCurrency: $prevCurrencyStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
